<template>
	<div class="container">
		<div class="page">
			<!-- Header -->
			<div class="page-header">
				<div class="row">
					<div class="col-md-9">
						<h1 class="page-title">{{ $t('Request Product Monitoring') }}</h1>
					</div>
				</div>
			</div>

			<!-- Overviews -->
			<div class="product-request-scoreboard">
				<div class="row" v-if="overview">
					<div class="col-md-3 col-6" v-for="(item, idx) in overview" :key="idx">
						<overview
							:text="item.label"
							:type="'normal'"
							:with-additional="false"
							:number="item.value"
							:clickable="true"
							:class="{ 'active': status === item.key }"
							:is-fetching="isFetchingStatistic"
							@click="overviewAction(item.key)">
						</overview>
					</div>
				</div>
			</div>

			<!-- Table Menu -->
			<div class="table-list-menu d-lg-flex align-items-center justify-content-between">

				<div class="table-actions d-flex align-items-center">
					<!-- Add Product Request -->
					<a href="/admin/request-product/new" class="btn btn-primary btn-sm flex-center mr-2 mb-0">
						<i class="material-icons">add</i>
						<span class="no-wrap">{{ $t('Request Product') }}</span>
					</a>

					<!-- Search Keyword -->
					<input type="text" class="input form-control form-control-sm products-search-box"
						:placeholder="`${$t('Search')}..`" @input="onKeywordChange" v-model="keyword">
				</div>

				<div class="table-filters text-right d-flex align-items-center justify-content-end">
					<!-- Clear Filter -->
					<div class="d-flex align-items-center">
						<button type="button" class="btn btn-plain btn-sm" @click="clearFilter" v-if="isFilterOn">
							{{ $t('Clear Filter') }}
						</button>
					</div>
					<!-- Date Filter -->
					<daterangepicker :presets="filterDatePresets" :start="since" :end="until" @selected="setFilterDate">
					</daterangepicker>
				</div>
			</div>

			<div class="products-list table-responsive">
				<table class="products-list-table table table-primary table-striped mb-0">
					<thead>
						<tr>
							<td width="5%">{{ $t('No.') }}</td>
							<td>{{ $t('Seller Data') }}</td>
							<td>{{ $t('Contact') }}</td>
							<td>{{ $t('Address') }}</td>
							<td>{{ $t('Products Photo') }}</td>
							<td>{{ $t('Sales') }}</td>
							<td>{{ $t('Date') }}</td>
							<td>{{ $t('Status') }}</td>
							<td>{{ $t('Action') }}</td>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(item, idx) in productRequests" :key="item._id.$oid">
							<td>{{ idx + 1 }}</td>
							<td class="mw-150">
								{{ item.name }}
								<br />
								{{ item.store_name }}
							</td>
							<td class="mw-150">
								{{ item.email }}
								<br />
								{{ item.phone }}
							</td>
							<td class="mw-250">
								{{ item.store_address }}
								<br />
								{{ formatCityLabel(item.store_origin) }}
							</td>
							<td>
								<div class="row mw-250">
									<div class="col px-1"
										v-for="(image, index) in item.product_images.slice(0, maxImageShown + 1)"
										:key="index + 0">
										<div v-if="image === '' || (index > maxImageShown - 1)" class="pc-image-empty square-img">
											<div class="image-upload-placeholder placeholder-height" :class="{ 'cursor-pointer': index >= maxImageShown }">
												<i class="fi fi-rr-picture image-placeholder fs-16"
													v-if="index < maxImageShown"></i>
												<div class="text-placeholder pt-0 fs-12">
													<span v-if="index < maxImageShown">
														Foto {{ index + 1 }}
													</span>
													<span v-else @click="editProductRequest(item)">
														2+
													</span>
												</div>
											</div>
										</div>
										<div v-else class="pc-image square-img cursor-pointer">
											<div class="img" :style="{ backgroundImage: `url('${image}')` }" @click="enlargeImage(image)">
											</div>
										</div>
									</div>
								</div>
							</td>
							<td class="mw-150">{{ formatSales(item) }}</td>
							<td>{{ formatDate(item.created_at) }}</td>
							<td>
								<span :class="`text-${formatStatus(item.status).color}`">{{
									formatStatus(item.status).label
								}}</span>
							</td>
							<td class="d-flex flex-nowrap text-primary">
								<i class="bi bi-chat-dots action-icon" @click="openNoteModal(item, $event)"></i>
								<i class="bi bi-whatsapp action-icon" @click="followUp(item)"></i>
								<i class="bi bi-card-text action-icon" @click="editProductRequest(item)"></i>
								<div v-if="isStatusUpdating" class="is-loading mx-4"></div>
								<div v-else>
									<i class="bi bi-pencil-square action-icon" data-toggle="dropdown"
										aria-expanded="false"></i>
									<div class="dropdown-menu">
										<button v-for="status in getStatusOptions(item.status)" :key="status.value"
											type="button" class="dropdown-item"
											@click="updateStatus(status.value, item._id.$oid, idx)"><span
												:class="`text-${status.color}`">{{ status.label }}</span></button>
									</div>
								</div>
							</td>
						</tr>
					</tbody>
				</table>

				<div class="list-empty table-empty" v-if="productRequests.length === 0 && isFetching === false">
					<i class="material-icons mr-2 va-m">error_outline</i>
					<span class="va-m">{{ $t('No product request was found') }}</span>
				</div>
			</div>

			<!-- Table Loader -->
			<div class="table-fetch-status" v-if="isFetching">
				<div class="is-loading large dark m-auto"></div>
			</div>

			<!-- Modals -->
			<div class="modals">

				<!-- Modal Detail -->
				<modal class="table-filter-modal" size="medium" @close="isShowEdit = false" v-if="isShowEdit">
					<div slot="header">
						<h3>Request Product Detail</h3>
					</div>
					<div slot="body">
						<div class="row">

							<div class="col-md-6">
								<div class="form-group">
									<label>Nama <span class="text-danger">*</span></label>
									<input type="text" class="form-control" :class="{ 'is-invalid': errorMessages.name }"
										placeholder="Masukkan Nama Anda..." v-model="name" required readonly />
									<small v-if="errorMessages.name" class="invalid-feedback">{{ errorMessages.name
									}}</small>
								</div>
							</div>

							<div class="col-md-6">
								<div class="form-group">
									<label>Nama Perusahaan / Toko</label>
									<input type="text" class="form-control"
										:class="{ 'is-invalid': errorMessages.storeName }"
										placeholder="Masukkan Nama Perusahaan..." v-model="storeName" required readonly />
									<small v-if="errorMessages.storeName" class="invalid-feedback">{{
										errorMessages.storeName
									}}</small>
								</div>
							</div>
							<div class="col-md-6">
								<div class="form-group">
									<label>Email Bisnis <span class="text-danger">*</span></label>
									<input type="email" class="form-control" :class="{ 'is-invalid': errorMessages.email }"
										placeholder="Masukkan Email Bisnis Anda..." v-model="email" required readonly />
									<small v-if="errorMessages.email" class="invalid-feedback">{{ errorMessages.email
									}}</small>
								</div>
							</div>

							<div class="col-md-6">
								<div class="form-group">
									<label>Nomor Telepon / WA <span class="text-danger">*</span></label>
									<div class="input-group">
										<div class="input-group-prepend">
											<span class="input-group-text">{{ countryCode }}</span>
										</div>
										<input type="text" name="phone" class="form-control"
											:class="{ 'is-invalid': errorMessages.phone }"
											placeholder="Masukkan Nomor Telepon Anda..." v-model.trim="phone"
											minlength="9" required readonly>
										<small v-if="errorMessages.phone" class="invalid-feedback">{{ errorMessages.phone
										}}</small>
									</div>
								</div>
							</div>

							<div class="col-md-12">
								<div class="form-group">
									<label>Alamat Lengkap <span class="text-danger">*</span></label>
									<textarea class="form-control" :class="{ 'is-invalid': errorMessages.address }"
										placeholder="Masukkan Alamat Lengkap..." rows="3" required
										v-model="address" readonly></textarea>
									<small v-if="errorMessages.address" class="invalid-feedback">{{ errorMessages.address
									}}</small>
								</div>
							</div>

							<!-- Categories -->
							<div class="col-md-12">
								<div class="form-group">
									<div class="mb-2">
										<label>Jenis Produk Yang Diminati <span class="text-danger">*</span></label>
										<multiselect placeholder="Select Category" label="name" track-by="slug"
											@select="onCategoryChange" @close="categoryValidation"
											v-model="selectedCategory" :options="categories" :show-labels="false"
											:allow-empty="false" :disabled="categories.length === 0">
										</multiselect>
										<small v-if="errorMessages.category" class="text-danger">{{ errorMessages.category
										}}</small>
									</div>

									<div>
										<multiselect v-if="subCategories" placeholder="Select Sub Category" label="name" track-by="slug"
											v-model="selectedSubCategory"
											:options="subCategories" :show-labels="false" :allow-empty="false"
											:disabled="subCategories.length === 0">
										</multiselect>
										<small v-if="errorMessages.subCategory" class="text-danger">{{
											errorMessages.subCategory }}</small>
									</div>
								</div>
							</div>

							<div class="col-md-12">
								<div class="form-group">
									<label>Deskripsi Produk (Spesifikasi Produk) <span class="text-danger">*</span></label>
									<textarea class="form-control" :class="{ 'is-invalid': errorMessages.description }"
										placeholder="Masukkan Deskripsi Produk..." rows="3" required
										v-model="description"></textarea>
									<small v-if="errorMessages.description" class="invalid-feedback">{{
										errorMessages.description }}</small>
								</div>
							</div>

							<div class="col-md-12">
								<div class="form-group">
									<label>Harga Rekomendasi <span class="text-danger">*</span></label>
									<div class="input-group">
										<div class="input-group-prepend">
											<div class="input-group-text">
												{{ currencySymbol }}
											</div>
										</div>
										<input type="text" class="input form-control" min="0"
											:class="{ 'is-invalid': errorMessages.price }" @input="onPriceChange"
											@change="onPriceChanges" v-model="price" />
										<small v-if="errorMessages.price" class="invalid-feedback">{{
											errorMessages.price }}</small>
									</div>
								</div>
							</div>

							<div class="row mx-0 w-100">
								<div class="col-md-12">
									<label>Contoh Foto Produk</label>
									<p class="pc-p">
										Format gambar .jpg .jpeg .png dan ukuran minimum 300 x 300px (Untuk gambar
										optimal gunakan ukuran minimum 700 x 700 px). Ukuran maksimum gambar adalah <b>2MB</b>.
									</p>
									<small v-if="errorMessages.image" class="text-danger">{{ errorMessages.image }}</small>
								</div>
								<div class="col-md-12">
									<div class="box__content">
										<div class="pc-images-list">
											<draggable draggable=".draggable" v-model="product.images" v-bind="dragOptions"
												@start="drag = true" @end="drag = false">
												<transition-group class="row small-gutter" type="transition"
													:name="!drag ? 'flip-list' : null">
													<div class="col" :class="{ draggable: image !== '' }"
														v-for="(image, index) in product.images" :key="index + 0">
														<div v-if="isLoading && image === ''" class="pc-image-empty">
															<div class="image-loading">
																<div class="is-loading"></div>
															</div>
														</div>
														<div v-else-if="image === ''" class="pc-image-empty"
															:class="{ 'cursor-pointer': isStatusReview }"
															@click="uploadImages(index)">
															<div class="image-upload-placeholder">
																<i class="fi fi-rr-picture image-placeholder"></i>
																<div class="text-placeholder">
																	Foto {{ index + 1 }}
																</div>
															</div>
														</div>
														<div v-else class="pc-image" :class="{
															'is-loading': image === '',
														}">
															<div class="img"
																:style="{ backgroundImage: `url('${image}')` }"
																@click="enlargeImage(image)">
															</div>
															<i class="material-icons" v-tooltip="'Remove'"
																v-if="isStatusReview"
																@click="removeProductImages(index)">close</i>
														</div>
													</div>
												</transition-group>
											</draggable>
										</div>
										<div class="form-group buttons m-0 hidden">
											<input type="file" accept="image/*" class="input-file" ref="imageUploader"
												id="pc-product-images-file" @change="onProductImagesChange" />
										</div>
									</div>
								</div>
							</div>

							<div class="col-md-12">
								<div class="form-group">
									<label>Link Referensi Produk</label>
									<input type="text" class="form-control" placeholder="https://"
										v-model.trim="referenceLink">
									<small v-if="errorMessages.referenceLink" class="text-danger">{{
										errorMessages.referenceLink
									}}</small>
								</div>
							</div>

							<div class="col-md-12">
								<div class="form-group">
									<label>{{ $t('Berapa rata-rata jumlah penjualan Anda?') }} <span
											class="text-danger">*</span></label>
									<div class="input-group">
										<div class="input-group-prepend">
											<button
												class="btn btn-outline-secondary dropdown-toggle"
												:class="{
													'border-error': errorMessages.selectedSalesPeriod,
													'border-normal': !errorMessages.selectedSalesPeriod,
												}"
												type="button"
												data-toggle="dropdown"
												aria-expanded="false"
											>
												{{ selectedSalesPeriodLabel }}
											</button>
											<div class="dropdown-menu">
												<a
													class="dropdown-item"
													@click="assignPeriod(period.value)"
													v-for="(period, idx) in salesPeriodOptions"
													:key="idx"
												>
													{{ period.label }}
												</a>
											</div>
										</div>
										<input
											type="text"
											class="input form-control"
											min="0"
											:class="{ 'is-invalid': errorMessages.salesValue }"
											@input="onSalesValueChange"
											@change="onSalesValueChanges"
											v-model="salesValue"
										/>
									</div>
									<div class="row">
										<small v-if="errorMessages.selectedSalesPeriod" class="col-md-12 text-danger">{{
											errorMessages.selectedSalesPeriod }}</small>
										<small v-if="errorMessages.salesValue" class="col-md-12 text-danger">{{
											errorMessages.salesValue }}</small>
									</div>
								</div>
							</div>

						</div>
					</div>
					<div slot="footer">
						<button type="button" class="btn btn-light" @click="isShowEdit = false">
							{{ $t('Close') }}
						</button>
						<button type="button" class="btn btn-primary" :class="{ 'is-loading': isUpdateProductRequest }"
							:disabled="!isStatusReview"
							v-tooltip="disabledTooltip"
							@click="updateProductRequest">
							Update
						</button>
					</div>
				</modal>

				<!-- Follow Up -->
				<follow-up-modal :name="followUpName" :number="followUpNumber" :message="followUpMessage"
					@close="hideFollowUp" v-if="isFollowUpVisible" />

				<!-- Modal Notes -->
				<div class="modals">
					<modal
						class="table-filter-modal"
						size="large"
						@close="isOpenNoteModal = false"
						v-if="isOpenNoteModal">
						<div slot="header">
							<h3>Notes Request Product</h3>
						</div>
						<div slot="body">
							<ticket-loading :is-fetching="isFetchingDetail" v-if="isFetchingDetail"/>
							<div class="ticket" v-else>
								<div class="ticket-info">
									<div class="info-row">
										<span class="text-bold">{{ $t('Seller Name') }} :</span>
										<span class="ml-1">{{ handleEmpty(selectedProductRequest.name) }}</span>
									</div>
									<div class="info-row">
										<span class="text-bold">{{ $t('Category') }} :</span>
										<span class="ml-1">{{ formatCategory(selectedProductRequest.category) }}</span>
									</div>
									<div class="info-row">
										<span class="text-bold">{{ $t('Status') }} :</span>
										<span class="ml-1">{{ getStatus(selectedProductRequest.status) }}</span>
									</div>
								</div>

								<ticket-message :list-message="listMessage" :user-data="user"/>

								<div class="form-group mb-1 mt-4">
									<label class="text-bold">
										Tulis catatan
									</label>
									<textarea
										class="form-control"
										rows="2"
										placeholder="Tulis pesan anda di sini"
										v-model="content">
									</textarea>
								</div>
							</div>
						</div>

						<div slot="footer">
							<button type="button" class="btn btn-light" @click="isOpenNoteModal = false">{{ $t('Close') }}</button>
							<button type="button" :class="{'is-loading' : isUpdating }" class="btn btn-primary" @click="saveMessage">Send</button>
						</div>
					</modal>
				</div>
			</div>

		</div>
		<notifications group="product-requests" position="bottom right" />
	</div>
</template>

<script>
import draggable from 'vuedraggable';
import { mapGetters } from 'vuex';
import adminApi, { getProductRequests, updateProductRequestStatus } from '@/api/admin';
import { updateProductRequest } from '@/api/product-request';
import statsApi from '@/api/statistic';
import homepageApi from '@/api/homepage';
import mediaApi from '@/api/media';
import Daterangepicker from '@/components/Daterangepicker.vue';
import Modal from '@/components/Modal.vue';
import Overview from '@/components/Overview.vue';
import FollowUpModal from '@/components/FollowUpModal.vue';
import TicketLoading from '@/components/admin/ticket/TicketLoading.vue';
import TicketMessage from '@/components/admin/ticket/TicketMessage.vue';
import {
	getDateRange,
	duplicateVar,
	formatWaNumber,
	getAxiosErrorMessage,
	isUrlValid,
	toTitleCase,
} from '@/lib/helper';
import { statusOptions, salesRangeOptions, salesPeriodOptions } from '@/lib/statics';

const defaultFilterDate = getDateRange();

export default {
	name: 'AdminProductRequest',
	components: {
		Daterangepicker,
		FollowUpModal,
		Modal,
		Overview,
		draggable,
		TicketLoading,
		TicketMessage,
	},
	data() {
		return {
			page: 1,
			keyword: '',
			limit: 50,
			sort_by: 'created_at',
			sort: 'desc',
			status: 'all',
			filterDate: defaultFilterDate,
			isAll: false,
			isFetching: false,
			isStatusUpdating: false,
			isShowEdit: false,
			isFetchingCategories: false,
			categories: [],
			subCategories: [],
			selectedCategory: null,
			selectedSubCategory: null,
			description: '',
			price: 0,
			isLoading: false, // is uploading images
			drag: false,
			dragOptions: {
				animation: 100,
			},
			product: {
				images: ['', '', '', '', ''],
			},
			productImages: [],
			productImagesLimit: 5,
			referenceLink: '',
			salesRangeOptions,
			salesPeriodOptions,
			selectedSalesPeriod: '',
			selectedSales: '',
			salesValue: 0,
			productRequests: [],
			isUpdateProductRequest: false,
			name: '',
			storeName: '',
			email: '',
			countryCode: '+62',
			phone: '',
			address: '',
			statusOptions,
			maxImageShown: 3,
			errorMessages: {
				name: '',
				storeName: '',
				email: '',
				phone: '',
				address: '',
				category: '',
				subCategory: '',
				description: '',
				price: '',
				image: '',
				referenceLink: '',
				selectedSales: '',
				salesValue: '',
				selectedSalesPeriod: '',
			},
			followUpNumber: '',
			followUpName: '',
			followUpMessage: '',
			followUpMessages: '',
			isOpenNoteModal: false,
			isFetchingDetail: false,
			isUpdating: false,
			listMessage: [],
			content: '',
			isFollowUpVisible: false,
			isFetchingStatistic: false,
			overview: {
				product_request_review_count: {
					key: 'review',
					label: 'Review',
					value: 0,
				},
				product_request_reject_count: {
					key: 'reject',
					label: 'Reject',
					value: 0,
				},
				product_request_approve_count: {
					key: 'approve',
					label: 'Approve',
					value: 0,
				},
				product_request_count: {
					key: 'all',
					label: 'Total',
					value: 0,
				},
			},
		};
	},
	methods: {
		handleEmpty(variable) {
			return variable ?? '-';
		},
		getStatus(status) {
			return toTitleCase(status.replace('_', ' '));
		},
		openNoteModal(ticket, event) {
			this.isFetchingDetail = true;

			if (event.target.tagName.toLowerCase() === 'button' || event.target.tagName.toLowerCase() === 'a') {
				return;
			}

			this.selectedProductRequest = duplicateVar(ticket);
			this.content = '';
			this.isOpenNoteModal = true;

			const params = {
				ticket_id: this.selectedProductRequest?._id?.$oid,
			};

			const callback = (response) => {
				this.isFetchingDetail = false;
				this.listMessage = response.data;
			};

			const errorCallback = () => {
				this.isFetchingDetail = false;
				this.$notify({
					group: 'app',
					title: 'Notes',
					text: 'Failed to fetch notes',
					type: 'error',
				});
			};

			adminApi.listTicketMessage(params, callback, errorCallback);
		},
		saveMessage() {
			this.isUpdating = true;

			const selectedTicket = duplicateVar(this.selectedProductRequest);
			selectedTicket._id = selectedTicket?._id?.$oid;

			const params = {
				ticket: selectedTicket,
				content: this.content,
				type: 'product-request',
			};

			const callback = (response) => {
				this.isUpdating = false;
				this.content = '';
				this.listMessage.push(response.data);
			};

			const errorCallback = () => {
				this.isUpdating = false;
				this.$notify({
					group: 'app',
					title: 'Notes',
					text: 'Failed to save message',
					type: 'error',
				});
			};

			adminApi.saveMessageTicket(params, callback, errorCallback);
		},
		handleScroll() {
			const pageHeight = document.documentElement.offsetHeight;
			const windowHeight = window.innerHeight;
			const scrollPosition = window.scrollY;
			const offset = 50;
			const isOnBottom = pageHeight - offset <= windowHeight + scrollPosition;
			if (isOnBottom) {
				if (!this.isFetching && !this.isAll) {
					this.fetchProductRequests(false);
				}
			}
		},
		fetchProductRequests(reset = true) {
			if (reset) {
				this.resetProductRequests();
			}

			this.isFetching = true;
			const params = this.params;

			const callback = (response) => {
				const productRequests = response.data;
				this.productRequests = this.productRequests.concat(productRequests);
				this.isAll = productRequests.length < this.limit;
				this.page++;
				this.isFetching = false;
			};
			const errorCallback = (e) => {
				const msg = getAxiosErrorMessage(e);
				// eslint-disable-next-line
				console.log(msg);
			};
			getProductRequests(params, callback, errorCallback);
		},
		resetProductRequests() {
			this.productRequests = [];
			this.isAll = false;
			this.page = 1;
		},
		formatCityLabel(customerAddress) {
			return `${customerAddress?.province?.name}, ${customerAddress?.city?.name}, ${customerAddress?.subdistrict?.name}`;
		},
		formatSales(item) {
			let salesLabel = '-';

			const sales = this.salesRangeOptions.find(rev => rev.value === item.sales);
			if (sales && sales.label) {
				salesLabel = sales.label;
			}

			if (salesLabel === '-' && item.sales) {
				salesLabel = item.sales;
			}

			const salesPeriod = item.sales_period ? `/ ${item.sales_period}` : '/ day';
			salesLabel = `${salesLabel} ${salesPeriod}`;

			return salesLabel;
		},
		formatCategory(category) {
			let categoryString = category.name;
			categoryString += category.sub_category ? ` - ${category.sub_category.name}` : '';
			return categoryString;
		},
		formatDate(dateString) {
			const dateNumberLong = date => new Date(parseInt(date.$date.$numberLong));
			const date = typeof dateString === 'object' ? dateNumberLong(dateString) : dateString;
			const timezone = this.user ? this.user.timezone : 'Asia/Jakarta';
			const formattedDate = this.moment
				.utc(date)
				.tz(timezone)
				.format('DD MMM YYYY');
			return dateString && dateString !== '' ? formattedDate : '-';
		},
		formatStatus(statusValue) {
			const selectedStatus = this.statusOptions.find(status => status.value === statusValue);
			return {
				label: selectedStatus?.label,
				color: selectedStatus?.color,
			};
		},
		onKeywordChange() {
			const self = this;
			this.ooDelay(() => {
				self.fetchProductRequests();
			}, 1000);
		},
		clearFilter() {
			window.location.href = '/admin/request-product';
		},
		initFilterDate() {
			if (this.since && this.since !== '' && this.until && this.until !== '') {
				const since = this.moment(this.since);
				const until = this.moment(this.until);
				const filterDate = {
					start: since,
					end: until,
				};
				this.filterDate = filterDate;
			}
		},
		setFilterDate(filterDate) {
			this.filterDate = filterDate;
			const format = 'YYYY-MM-DD';
			const since = this.moment(filterDate.start).format(format);
			const until = this.moment(filterDate.end).format(format);
			const query = duplicateVar(this.$route.query);
			query.since = since;
			query.until = until;
			const params = { query };
			this.$router.push(params);
			this.fetchProductRequests();
		},
		getStatusOptions(statusValue) {
			return this.statusOptions.filter(status => status.value !== statusValue);
		},
		updateStatus(status, productRequestId, idx) {
			this.isStatusUpdating = true;
			const params = {
				status,
			};

			const callback = (response) => {
				const message = response.message;
				const productRequest = this.productRequests[idx];
				productRequest.status = status;
				this.productRequests[idx] = productRequest;
				this.$notify({
					group: 'product-requests',
					type: 'success',
					title: 'Success',
					text: message,
				});
				this.isStatusUpdating = false;
			};

			const errorCallback = (e) => {
				const msg = getAxiosErrorMessage(e);
				this.$notify({
					group: 'product-requests',
					type: 'error',
					title: 'Failed',
					text: msg,
				});
				console.log(msg);
				this.isStatusUpdating = false;
			};

			updateProductRequestStatus(productRequestId, params, callback, errorCallback);
		},
		// eslint-disable-next-line camelcase
		cityLabel({ province_name, city_name_with_type, subdistrict_name }) {
			// eslint-disable-next-line camelcase
			return `${province_name}, ${city_name_with_type}, ${subdistrict_name}`;
		},
		categoryValidation() {
			if (this.selectedCategory
				&& this.selectedCategory.slug !== 'uncategorized'
				&& this.selectedCategory.slug !== 'produk-lainnya') {
				this.errorMessages.category = '';
			} else {
				this.errorMessages.category = 'Kategori Wajib Dipilih';
			}
		},
		subCategoryValidation() {
			if (this.selectedSubCategory) {
				this.errorMessages.subCategory = '';
			} else {
				this.errorMessages.subCategory = 'Sub Kategori Wajib Dipilih';
			}
		},
		descriptionValidation() {
			if (this.description.length <= 0) {
				this.errorMessages.description = 'Deskripsi Wajib Diisi';
			} else {
				this.errorMessages.description = '';
			}
		},
		priceValidation() {
			if (parseInt(this.price) === 0 || this.price.length <= 0) {
				this.errorMessages.price = 'Harga Wajib Diisi';
			} else {
				this.errorMessages.price = '';
			}
		},
		fetchCategories() {
			this.isFetchingCategories = true;
			const callback = (response) => {
				this.isFetchingCategories = false;
				const categories = response.data;
				this.categories = categories;
			};
			const errorCallback = () => {
				this.isFetchingCategories = false;
			};
			homepageApi.getAllCategories(callback, errorCallback);
		},
		onCategoryChange(category) {
			this.selectedSubCategory = null;
			this.subCategories = category?.sub_categories;
		},
		parsePricing(price) {
			let parsedPrice = price.replace(/[^0-9]/g, '').trim();
			const parsedPriceLength = parsedPrice.length;
			if (parsedPriceLength > 1 && parsedPrice.charAt(0) === '0') {
				parsedPrice = parsedPrice.substring(1, parsedPriceLength);
			}
			return parsedPrice;
		},
		onPriceChange() {
			this.price = this.parsePricing(this.price);
		},
		onPriceChanges() {
			if (this.price === '') {
				this.price = 0;
			}
		},
		onSalesValueChange() {
			this.salesValue = this.parsePricing(this.salesValue);
		},
		onSalesValueChanges() {
			if (this.salesValue === '') {
				this.salesValue = 0;
			}
		},
		uploadImages(index) {
			if (!this.isStatusReview) {
				return;
			}
			this.currentIndexUploadImage = index;
			const inputFile = this.$refs.imageUploader;
			inputFile.click();
		},
		onProductImagesChange(event) {
			this.isLoading = true;
			const maxUploadSize = 2; // MB
			const input = event.target;
			for (let i = 0; i < input.files.length; i++) {
				const image = input.files[i];

				// Check File Size
				let fileSize = image.size;
				fileSize /= 1048576;

				// Limit Images
				const isLimitReached =
					this.productImages.length === this.productImagesLimit;
				const isFileSizeTooLarge = fileSize > maxUploadSize;

				// Show Notice if size too large
				if (isFileSizeTooLarge) {
					const noticeMessage = `'${image.name}' size is too large (max 2MB)`;
					this.$notify({
						group: 'app',
						text: noticeMessage,
						type: 'warn',
					});
					this.isLoading = false;
					break;
				}

				if (isLimitReached) {
					console.log('Maximum 5 Images');
					const noticeMessage = 'Maksimum upload 5 foto produk';
					this.$notify({
						group: 'app',
						text: noticeMessage,
						type: 'warn',
					});
					this.isLoading = false;
					break;
				}

				this.productImages.push(image);

				const previewIndex = this.currentIndexUploadImage;

				this.$set(this.product.images, previewIndex, '');

				// Load the image file into a canvas element
				const img = new Image();
				const canvas = document.createElement('canvas');
				const ctx = canvas.getContext('2d');
				const reader = new FileReader();
				// eslint-disable-next-line no-loop-func
				reader.onload = () => {
					img.onload = () => {
						const size = Math.max(img.width, img.height);
						canvas.width = size;
						canvas.height = size;
						const offsetX = (size - img.width) / 2;
						const offsetY = (size - img.height) / 2;
						ctx.fillStyle = '#fff';
						ctx.fillRect(0, 0, size, size); // fill the canvas with white
						ctx.drawImage(img, offsetX, offsetY);

						// Convert the canvas to a blob and upload it to the server
						canvas.toBlob((blob) => {
							// Start Upload
							const uploadParams = new FormData();
							uploadParams.append('image', blob);

							const callback = (response) => {
								this.isLoading = false;
								const path = response.data;
								const url = `${this.mediaUrlPrefix}/${path}`;
								this.$set(this.product.images, previewIndex, url);
								this.linkImageValidation();
							};

							const errorCallback = () => {
								this.isLoading = false;
								// eslint-disable-next-line
								this.productImages.splice(previewIndex, 1);
								this.product.images.splice(previewIndex, 1);
								this.linkImageValidation();
							};

							// Upload Image
							mediaApi.uploadImagePublic(uploadParams, callback, errorCallback);
						}, image.type, 0.9);
					};
					img.src = reader.result;
				};
				reader.readAsDataURL(image);
			}
			event.target.files = null;
			event.target.value = '';
		},
		removeProductImages(index) {
			this.productImages.splice(index, 1);
			this.product.images.splice(index, 1, '');
		},
		linkImageValidation() {
			const image = this.product.images.find(img => img !== '');
			if (!image && !this.referenceLink) {
				this.errorMessages.image = 'Upload minimal 1 gambar atau isi Link Referensi';
				this.errorMessages.referenceLink = 'Isi Link Referensi atau Upload minimal 1 gambar';
			} else if (this.referenceLink && !isUrlValid(this.referenceLink)) {
				this.errorMessages.referenceLink = 'Link harus valid dan diawali https:// atau http://';
			} else {
				this.errorMessages.image = '';
				this.errorMessages.referenceLink = '';
			}
		},
		salesValidation() {
			if (this.selectedSales === '') {
				this.errorMessages.selectedSales = 'Total Penjualan Per Hari Wajib Dipilih';
			} else {
				this.errorMessages.selectedSales = '';
			}
		},
		salesValueValidation() {
			if (parseInt(this.salesValue) === 0 || this.salesValue.length <= 0) {
				this.errorMessages.salesValue = 'Rata-rata Jumlah Penjualan Wajib Diisi';
			} else {
				this.errorMessages.salesValue = '';
			}
		},
		salesPeriodValidation() {
			this.errorMessages.selectedSalesPeriod = '';

			if (this.selectedSalesPeriod === '') {
				this.errorMessages.selectedSalesPeriod = 'Periode Wajib Dipilih';
			}
		},
		validateAllForm() {
			this.categoryValidation();
			// this.subCategoryValidation();
			this.descriptionValidation();
			this.priceValidation();
			this.linkImageValidation();
			// this.salesValidation();
			this.salesValueValidation();
			this.salesPeriodValidation();
		},
		assignCategory(selectedProductRequest) {
			this.selectedCategory = selectedProductRequest?.category;
			const categories = this.categories.find(category => category.slug === this.selectedCategory.slug);
			this.onCategoryChange(categories);
			const category = selectedProductRequest?.category;
			const subCategory = category.sub_category ? category.sub_category : null;
			this.selectedSubCategory = subCategory;
		},
		editProductRequest(productRequest) {
			this.selectedProductRequest = productRequest;
			this.name = this.selectedProductRequest?.name;
			this.storeName = this.selectedProductRequest?.store_name;
			this.email = this.selectedProductRequest?.email;
			this.phone = this.selectedProductRequest?.phone.startsWith('+62') ? this.selectedProductRequest?.phone.replace('+62', '') : this.selectedProductRequest?.phone;
			this.address = `${this.selectedProductRequest?.store_address}, ${this.formatCityLabel(this.selectedProductRequest?.store_origin)}`;
			this.assignCategory(this.selectedProductRequest);
			this.description = this.selectedProductRequest?.description || '';
			this.price = this.selectedProductRequest?.price || '';
			this.product.images = this.selectedProductRequest?.product_images;
			this.referenceLink = this.selectedProductRequest?.reference_link;
			this.selectedSales = this.selectedProductRequest?.sales;
			this.salesValue = this.selectedSales;
			this.selectedSalesPeriod = this.selectedProductRequest?.sales_period ?? '';
			this.isShowEdit = true;
		},
		getCategoryParams() {
			const selectedSubCategory = duplicateVar(this.selectedSubCategory);
			const selectedCategory = duplicateVar(this.selectedCategory);
			delete selectedSubCategory.sub_sub_categories;
			delete selectedCategory.sub_categories;
			delete selectedCategory._id;
			delete selectedSubCategory._id;
			delete selectedSubCategory.parent_id;

			const category = {
				...selectedCategory,
				sub_category: selectedSubCategory,
			};

			return category;
		},
		updateProductRequest() {
			this.validateAllForm();
			const isErrorExist = Object.values(this.errorMessages).find(val => val !== '');
			if (isErrorExist) {
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Failed',
					text: 'Form harus diisi.',
				});
				return;
			}

			this.isUpdateProductRequest = true;

			const category = this.getCategoryParams();

			const params = {
				...this.selectedProductRequest,
				category,
				description: this.description,
				price: parseInt(this.price),
				product_images: this.product.images,
				reference_link: this.referenceLink,
				sales: this.salesValue,
				sales_period: this.selectedSalesPeriod,
			};

			const callback = (response) => {
				const message = response.message;
				this.$notify({
					group: 'app',
					title: 'Update Request Product',
					text: message,
					type: 'success',
				});
				this.isUpdateProductRequest = false;
				this.isShowEdit = false;
				this.fetchProductRequests();
			};

			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					title: 'Save Request Product',
					text: message,
					type: 'error',
				});
				this.isUpdateProductRequest = false;
			};

			updateProductRequest(this.selectedProductRequest._id.$oid, params, callback, errorCallback);
		},
		followUp(item) {
			this.generateFollowUp(item);
			this.isFollowUpVisible = true;
		},
		hideFollowUp() {
			this.followUpNumber = '';
			this.followUpName = '';
			this.followUpMessage = '';
			this.isFollowUpVisible = false;
		},
		assignPeriod(value) {
			this.selectedSalesPeriod = value;
		},
		enlargeImage(src) {
			const overlay = document.createElement('div');
			overlay.style.cssText = `
				background: rgba(0, 0, 0, 0.5) url(${src}) no-repeat center;
				background-size: contain;
				width: 100%;
				height: 100%;
				position: fixed;
				top: 0;
				left: 0;
				z-index: 10000;
				cursor: zoom-out;
			`;
			overlay.addEventListener('click', () => {
				document.body.removeChild(overlay);
			});
			document.body.appendChild(overlay);
		},
		generateFollowUp(item) {
			const phone = item.phone;
			const customerName = item.name || '';
			const followUpMessages = '';
			this.followUpNumber = formatWaNumber(phone);
			this.followUpName = customerName;
			this.followUpMessage = followUpMessages;
		},
		fetchStats() {
			this.isFetchingStatistic = true;

			const params = {
				items: 'product_requests',
			};

			const callback = (response) => {
				const stats = response.data?.product_requests;

				if (stats) {
					Object.keys(stats).forEach((key) => {
						const value = stats[key] ?? 0;
						this.overview[key].value = value;
					});
				}

				this.isFetchingStatistic = false;
			};

			const errorCallback = (e) => {
				console.log(e);

				this.isFetchingStatistic = false;
			};

			statsApi.getAppStatsNew(params, callback, errorCallback);
		},
		overviewAction(key) {
			this.status = key;
			this.fetchProductRequests();
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			filterDatePresets: 'filterDatePresets',
			currency: 'currency',
		}),
		since() {
			const since = this.$route.query.since;
			return since;
		},
		until() {
			const until = this.$route.query.until;
			return until;
		},
		params() {
			const dateFormat = 'YYYY-MM-DD';
			const startDate = this.moment(this.filterDate.start).format(dateFormat);
			const endDate = this.moment(this.filterDate.end).format(dateFormat);

			const params = {
				limit: this.limit,
				sort_by: this.sort_by,
				sort: this.sort,
				page: this.page,
				since: startDate,
				until: endDate,
			};

			if (this.keyword && this.keyword !== '') {
				params.keyword = this.keyword;
			}

			if (this.status !== 'all') {
				params.status = this.status;
			}

			return params;
		},
		isFilterOn() {
			return (
				this.since
				|| this.until
				|| this.keyword.length > 0
			);
		},
		currencySymbol() {
			return this.getCurrencySymbol(this.currency);
		},
		isStatusReview() {
			const isReview = this.selectedProductRequest?.status === 'review';

			return isReview;
		},
		disabledTooltip() {
			const tooltip = !this.isStatusReview ? this.$t('Data can only be edited when in \'review\' status') : '';

			return tooltip;
		},
		selectedSalesPeriodLabel() {
			let label = 'Periode';
			const period = this.salesPeriodOptions.find(period => period.value === this.selectedSalesPeriod);

			if (period && period.label) {
				label = period.label;
			}

			return label;
		},
	},
	created() {
		this.initFilterDate();
		this.fetchProductRequests();
		this.fetchCategories();
		this.fetchStats();
		// On Scroll Bottom
		window.addEventListener('scroll', this.handleScroll);
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll);
	},
};
</script>

<style scoped>
.action-icon {
	font-size: 24px;
	cursor: pointer;
	margin-left: 0.5rem !important;
	margin-right: 0.5rem !important;
}

.border-normal {
	border: 1px solid #ced4da;
}

.border-error {
	border: 1px solid red;
}

.no-wrap {
	white-space: nowrap;
}

.mw-150 {
	min-width: 150px;
}

.mw-250 {
	min-width: 250px;
}

.square-img {
	max-width: 60px;
	max-height: 60px;
}

.fs-16 {
	font-size: 16px !important;
}

.fs-12 {
	font-size: 12px !important;
}

.placeholder-height {
	height: 60px;
}

</style>
